import { Box, Stack, styled } from '@mui/material';
import { uploadFile } from 'cloudinary';
import { DEFAULT_ASPECT_OPTIONS, ImageEditorModal, useImageEditorUpload } from 'components/Ui-V3/ImageEditor';
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from 'constants/validation';
import CloseIcon from 'images/icons/close.svg?react';
import ImageAddIcon from 'images/icons/image-add-01.svg?react';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { EColors, getColor } from 'theme';
import { UploadContent } from './UploadContent';

const UploadContainer = styled(Box)(({ theme: { spacing } }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    border: '1px dashed ' + getColor(EColors.v3Dark, 0),
    '&:hover': {
        border: '1px dashed ' + getColor(EColors.v3Dark, 0.2),
        borderRadius: '12px',
    },
}));

const ImageContainer = styled(Box, {
    shouldForwardProp: prop => prop !== 'isBrandBackground',
})<{ isBrandBackground: boolean }>(({ isBrandBackground }) => ({
    width: '64px',
    minWidth: '64px',
    height: '64px',
    borderRadius: '12px',
    backgroundColor: isBrandBackground ? getColor(EColors.brand) : getColor(EColors.v3Dark),
    padding: '4px',
    position: 'relative',
}));

const CloseContainer = styled(Box)({
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    backgroundColor: getColor(EColors.v3Border),
    borderRadius: '50%',
    cursor: 'pointer',
    zIndex: 1,
    '&:hover': {
        transform: 'scale(1.3)',
    },
});

const EmptyImageContainer = styled(ImageContainer, {
    shouldForwardProp: prop => prop !== 'isBrandBackground',
})<{ isBrandBackground: boolean }>(({ isBrandBackground }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isBrandBackground ? getColor(EColors.brand) : getColor(EColors.v3Dark),
}));

type UploadedFile = {
    cloudinaryId: string;
    url: string;
    title: string;
};

type ImageUploadFieldProps = {
    disabled?: boolean;
    value: string;
    isBrandBackground?: boolean;
    onChange: (file: UploadedFile) => void;
    defaultImage?: React.ReactNode;
    overrideEmptyImage?: boolean;
    hideCloseIcon?: boolean;
    uploadSuffix?: string;
    /** When set, opens a crop editor after initial upload. Value is the aspect ratio (e.g. 1 for square, 16/6 for banner). */
    cropAspect?: number;
    /** Modal title shown in the crop editor. Defaults to 'Adjust Image'. */
    cropTitle?: string;
    showImageEditor?: boolean;
};

export function ImageUploadField({
    value,
    disabled = false,
    onChange,
    isBrandBackground = false,
    defaultImage = null,
    overrideEmptyImage = false,
    hideCloseIcon = false,
    uploadSuffix,
    showImageEditor = false,
    cropAspect,
    cropTitle = 'Adjust Image',
}: ImageUploadFieldProps) {
    const [isUploading, setIsUploading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const onCroppedImageChange = useCallback(
        (image: { url: string; title?: string }) => {
            onChange({ cloudinaryId: '', url: image.url, title: image.title || '' });
        },
        [onChange]
    );

    const {
        showEditor,
        editorImage,
        rotation,
        isLoading: isCropLoading,
        isCropReady,
        openEditor,
        closeEditor,
        confirmAndUpload,
        setRotation,
        setCroppedAreaPixels,
    } = useImageEditorUpload({ onImageChange: showImageEditor ? onCroppedImageChange : undefined });

    const isDropBlocked = disabled || isUploading || showEditor || isCropLoading;

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (isDropBlocked) return;

            // Dropzone fires onDrop for every drop, including one where it rejected every
            // file — onDropRejected tells the user why, so there is nothing to do here.
            const [file] = acceptedFiles;
            if (!file) return;

            if (showImageEditor) {
                // Read the file as a data URL so the cropped result is uploaded to Cloudinary only once on confirm.
                // Unlike a blob URL, a data URL is a plain string and requires no cleanup.
                const reader = new FileReader();
                reader.onload = e => {
                    if (e.target?.result) openEditor({ url: e.target.result as string, title: file.name });
                };
                reader.readAsDataURL(file);
                return;
            }

            try {
                setIsUploading(true);
                const cloudinaryResponse = await uploadFile(file);
                onChange({
                    cloudinaryId: cloudinaryResponse.cloudinaryId || '',
                    title: cloudinaryResponse.title || '',
                    url: cloudinaryResponse.url || '',
                });
            } finally {
                setIsUploading(false);
            }
        },
        [isDropBlocked, onChange, showImageEditor, openEditor]
    );

    const onDropRejected = useCallback(
        (fileRejections: FileRejection[]) => {
            const fileTooLarge = fileRejections.some(r => r.errors.some(e => e.code === 'file-too-large'));
            const invalidType = fileRejections.some(r => r.errors.some(e => e.code === 'file-invalid-type'));
            const tooManyFiles = fileRejections.some(r => r.errors.some(e => e.code === 'too-many-files'));
            const snackbarOpts = {
                variant: 'error' as const,
                anchorOrigin: { horizontal: 'center' as const, vertical: 'top' as const },
            };
            if (invalidType) {
                enqueueSnackbar('Only PNG and JPG images are allowed. Please choose a valid file.', snackbarOpts);
            }
            if (fileTooLarge) {
                enqueueSnackbar(
                    `Image must be ${IMAGE_UPLOAD_MAX_MB} MB or smaller. Please choose a smaller file.`,
                    snackbarOpts
                );
            }
            if (tooManyFiles) {
                enqueueSnackbar('Please drop only one image at a time.', snackbarOpts);
            }
        },
        [enqueueSnackbar]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        multiple: false,
        maxSize: IMAGE_UPLOAD_MAX_BYTES,
        disabled: isDropBlocked,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpeg', '.jpg'],
        },
    });

    const onClear = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onChange({ cloudinaryId: '', url: '', title: '' });
    };

    return (
        <Stack gap={1} width="100%">
            <UploadContainer {...getRootProps()}>
                <input {...getInputProps()} />
                {value && !overrideEmptyImage ? (
                    <ImageContainer isBrandBackground={isBrandBackground}>
                        <img src={value} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        {!hideCloseIcon && (
                            <CloseContainer onClick={onClear}>
                                <CloseIcon style={{ width: '16px', height: '16px' }} />
                            </CloseContainer>
                        )}
                    </ImageContainer>
                ) : (
                    <EmptyImageContainer isBrandBackground={isBrandBackground}>
                        {defaultImage ? (
                            defaultImage
                        ) : (
                            <ImageAddIcon style={{ width: '20px', height: '20px', fill: 'transparent' }} />
                        )}
                    </EmptyImageContainer>
                )}
                <UploadContent
                    isDragActive={isDragActive}
                    isUploading={isUploading}
                    uploadSuffix={uploadSuffix}
                    maxSizeMb={IMAGE_UPLOAD_MAX_MB}
                />
            </UploadContainer>

            {showImageEditor && (
                <ImageEditorModal
                    open={showEditor}
                    image={editorImage}
                    rotation={rotation}
                    isLoading={isCropLoading}
                    isCropReady={isCropReady}
                    title={cropTitle}
                    aspect={cropAspect}
                    aspectOptions={cropAspect === undefined ? DEFAULT_ASPECT_OPTIONS : undefined}
                    onClose={closeEditor}
                    onConfirm={confirmAndUpload}
                    setRotation={setRotation}
                    setCroppedAreaPixels={setCroppedAreaPixels}
                    backgroundColor={isBrandBackground ? getColor(EColors.brand) : getColor(EColors.v3Dark)}
                />
            )}
        </Stack>
    );
}
